import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth as authApi, setApiToken, type User } from '../shared/api'

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, name: string, companyName: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loading: boolean
}

const AuthContext = createContext<AuthContextType>(null!)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // On app init / page refresh: restore session from httpOnly cookie via /api/auth/me
  useEffect(() => {
    authApi.me()
      .then((u) => {
        setUser(u)
      })
      .catch(() => {
        // No valid cookie session — user needs to log in
        setToken(null)
        setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  // Keep module-level API token in sync with React state
  useEffect(() => {
    setApiToken(token)
  }, [token])

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    // Server sets httpOnly cookie automatically on login response
    // Store token in memory only (NOT localStorage)
    setToken(res.access_token)
    setUser(res.user)
    // Demo account only: arm the first-run onboarding tour for this login.
    // OnboardingTour (mounted in DashboardLayout) consumes this flag once.
    if (res.user?.email === 'demo') {
      try { sessionStorage.setItem('lngperf:start_tour', '1') } catch { /* noop */ }
    }
  }

  const signup = async (email: string, name: string, companyName: string, password: string) => {
    const res = await authApi.signup({ email, name, company_name: companyName, password })
    // Backend sets httpOnly cookie + returns token. User is auto-logged-in.
    setToken(res.access_token)
    setUser(res.user)
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Best-effort — clear local state regardless
    }
    // Clear namespaced localStorage keys for current user
    if (user) {
      const userId = user.id
      try {
        localStorage.removeItem(`vr_visible_cols_${userId}`)
        localStorage.removeItem(`lngops_pinned_vessels_${userId}`)
        // Clear any remaining lngops_ prefixed keys
        const keysToRemove: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          if (k && k.startsWith('lngops_')) keysToRemove.push(k)
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k))
      } catch { /* empty */ }
    }
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true })
    }
  }, [user, loading, navigate])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg-primary">
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    )
  }

  if (!user) return null

  return <>{children}</>
}
